import { utils } from '@hh/global.js'

/**
 * data-state values:
 * - initial: haven't scrolled yet
 * - top: at the top of the page
 * - hidden: scrolling down past the header (announcement tucked away)
 * - revealed: scrolling up
 */

const STATES = {
  INITIAL: 'initial',
  TOP: 'top',
  HIDDEN: 'hidden',
  REVEALED: 'revealed',
}

class HHStickyHeader extends HTMLElement {
  constructor() {
    super()

    this.headerBounds = {}
    this.currentScrollTop = 0
    this._announcementHeight = 0
    this._state = STATES.INITIAL
    this.fullbleedObserver = null

    this.onScrollHandler = this.onScroll.bind(this)
    this.setSpacerHandler = this.setSpacerHeight.bind(this)
    this.handleOpenOrbe = this.handleOpenOrbe.bind(this)
  }

  get swans() {
    return this.hasAttribute('swans')
  }

  get blog() {
    return this.hasAttribute('blog')
  }

  get headerEl() {
    return this.querySelector('.js-header')
  }

  get headerContainerEl() {
    return this.querySelector('.js-header-container')
  }

  getHeaderContainerHeight() {
    const container = this.headerContainerEl
    if (!container) return this.headerEl?.getBoundingClientRect().height || 0

    // Include absolutely positioned sub-nav children in the measured height
    const containerRect = container.getBoundingClientRect()
    let bottom = containerRect.bottom

    container.querySelectorAll('.js-header, .js-primary-sub-nav, .js-sub-nav').forEach((el) => {
      const style = window.getComputedStyle(el)
      if (style.display === 'none' || style.visibility === 'hidden') return
      bottom = Math.max(bottom, el.getBoundingClientRect().bottom)
    })

    return Math.max(0, bottom - containerRect.top)
  }

  connectedCallback() {
    if (!this.hasAttribute('data-state')) {
      this.dataset.state = STATES.INITIAL
    }

    window.addEventListener('scroll', this.onScrollHandler, { passive: true })
    window.addEventListener('resize', this.setSpacerHandler)
    document.addEventListener('openOrbe', this.handleOpenOrbe)
    this.createObserver()
    setTimeout(() => {
      this.setSpacerHeight()
    }, 0)
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler)
    window.removeEventListener('resize', this.setSpacerHandler)
    document.removeEventListener('openOrbe', this.handleOpenOrbe)
    this.fullbleedObserver?.disconnect()
  }

  setState(state) {
    if (this._state === state) return
    this._state = state
    this.dataset.state = state
  }

  createObserver() {
    const observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect
      observer.disconnect()
    })

    observer.observe(this)
  }

  getVisibleFullbleeds() {
    return [...document.querySelectorAll('[data-fullbleed]')].filter((el) => {
      const style = window.getComputedStyle(el)
      return style.display !== 'none' && style.visibility !== 'hidden' && el.getClientRects().length > 0
    })
  }

  observeFullbleed() {
    if (this.swans || this.blog) return

    const fullbleeds = this.getVisibleFullbleeds()
    // Observe the last stacked fullbleed so the header stays transparent through the whole group
    const fullbleed = fullbleeds[fullbleeds.length - 1]
    if (!fullbleed) {
      this.fullbleedObserver?.disconnect()
      this.setPastFullbleed(false)
      return
    }

    const headerContainerHeight = this.getHeaderContainerHeight()

    this.fullbleedObserver?.disconnect()
    this.fullbleedObserver = new IntersectionObserver(
      ([entry]) => {
        const pastFullbleed = !entry.isIntersecting && entry.boundingClientRect.top < 0
        this.setPastFullbleed(pastFullbleed)
      },
      {
        rootMargin: `-${headerContainerHeight}px 0px 0px 0px`,
        threshold: 0,
      }
    )

    this.fullbleedObserver.observe(fullbleed)
  }

  setPastFullbleed(past) {
    if (past) {
      this.setAttribute('data-past-fullbleed', '')
    } else {
      this.removeAttribute('data-past-fullbleed')
    }
  }

  setSpacerHeight() {
    const headerHeight = this.querySelector('.js-header')?.getBoundingClientRect().height || 0
    const headerContainerHeight = this.getHeaderContainerHeight()
    const subNavHeight = this.querySelector('.js-sub-nav')?.getBoundingClientRect().height || 0
    const primarySubNavHeight = this.querySelector('.js-primary-sub-nav')?.getBoundingClientRect().height || 0
    const announcementHeight = this.querySelector('.js-announcement-bar')?.getBoundingClientRect().height || 0
    const mobileBlogBarHeight = document.querySelector('.js-blog-bar-mobile')?.getBoundingClientRect().height || 0
    const blogMenuHeight = this.querySelector('.js-blog-menu')?.getBoundingClientRect().height || 0

    this._announcementHeight = announcementHeight

    const root = document.documentElement
    root.style.setProperty('--hh-announcement-height', `${announcementHeight}px`)
    root.style.setProperty('--hh-header-height', `${headerHeight}px`)
    root.style.setProperty('--hh-header-container-height', `${headerContainerHeight}px`)
    root.style.setProperty('--hh-mobile-blog-bar-height', `${mobileBlogBarHeight}px`)
    root.style.setProperty('--hh-sub-nav-height', `${subNavHeight}px`)
    root.style.setProperty('--hh-primary-sub-nav-height', `${primarySubNavHeight}px`)
    const section = this.closest('.shopify-section--header') || this
    section.style.setProperty('--hh-sticky-header-offset', `${announcementHeight}px`)

    this.setState(STATES.TOP)
    this.observeFullbleed()

    if (this.blog) {
      root.style.setProperty('--hh-blog-gradient-height', `${headerHeight + blogMenuHeight}px`)
    }
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop

    if (this.swans) {
      this.classList.toggle('scrolled', window.scrollY > 0)
      return
    }

    if (this.blog) {
      const headerHeight = this.querySelector('.js-header')?.getBoundingClientRect().height
      document.documentElement.style.setProperty('--hh-header-height', `${headerHeight}px`)
      this.classList.toggle('scrolled', window.scrollY > 0)
      return
    }

    if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      requestAnimationFrame(this.hide.bind(this))
    } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      requestAnimationFrame(this.reveal.bind(this))
    } else if (scrollTop <= this.headerBounds.top) {
      requestAnimationFrame(this.reset.bind(this))
    }

    this.currentScrollTop = scrollTop
  }

  hide() {
    if (this._state === STATES.HIDDEN) return

    this.setState(STATES.HIDDEN)
    document.documentElement.style.setProperty('--hh-announcement-height', '0px')

    utils.all('[open]', this).forEach((element) => {
      element.setAttribute('open', false)
    })
  }

  reveal() {
    if (this._state === STATES.REVEALED) return

    document.documentElement.style.setProperty(
      '--hh-announcement-height',
      `${this._announcementHeight}px`
    )
    this.setState(STATES.REVEALED)
  }

  reset() {
    if (this._state === STATES.TOP) return
    this.setState(STATES.TOP)
  }

  handleOpenOrbe() {
    setTimeout(() => {
      orbito.openAndRefreshModal()
      document.querySelector('.md-modal-closeButton')?.focus()
    }, 10)
  }
}

customElements.define('hh-sticky-header', HHStickyHeader)
